import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceType, ServiceSolution, SERVICE_TO_SOLUTIONS, ServiceStatus, CreateServiceSubscriptionRequest, ServiceSubscriptionFormData, INITIAL_FORM_DATA } from '../../types/service';
import { Network, Domain } from '../../types';
import axiosInstance from '../../lib/api/axiosInstance';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Save, X, Plus, Globe } from 'lucide-react';
import { ServiceFormFields } from './ServiceFormFields';
import { quickCreateWithDomain, createService } from '../../services/services';

interface QuickAddServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: { id: number; name: string; url: string } | null;
  missingService: {
    domainId: number;
    domain: string;
    missingService: string; // ServiceType
    identifierUsed: string;
  } | null;
  onCreated: () => Promise<void> | void;
}

export const QuickAddServiceModal: React.FC<QuickAddServiceModalProps> = ({
  open,
  onOpenChange,
  domain,
  missingService,
  onCreated
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ServiceSubscriptionFormData>({ ...INITIAL_FORM_DATA });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pour sélection network/domain quand domainId = 0
  const [networks, setNetworks] = useState<Network[]>([]);
  const [selectedNetworkId, setSelectedNetworkId] = useState<number | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);

  // Mode création de nouveau domaine
  const [createNewDomain, setCreateNewDomain] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainUrl, setNewDomainUrl] = useState('');

  const needsDomainSelection = missingService?.domainId === 0;

  useEffect(() => {
    if (open) {
      // Reset form when opening
      setFormData({ ...INITIAL_FORM_DATA, startDate: new Date().toISOString().split('T')[0] });
      setError(null);
      setSelectedNetworkId(null);
      setSelectedDomainId(null);
      setDomains([]);
      setCreateNewDomain(false);
      setNewDomainName('');
      setNewDomainUrl('');

      // Set default solution based on service type (after any restriction logic below)
      if (missingService) {
        const st = missingService.missingService as ServiceType;
        let allowed = SERVICE_TO_SOLUTIONS[st] || [];
        // In audit context restrict some solutions
        if (st === ServiceType.ADBLOCK) {
          // Prefer WORKAROUND (the one produced by audit for websiteName diff)
          allowed = [ServiceSolution.ADBLOCK_WORKAROUND, ...allowed.filter(a => a !== ServiceSolution.ADBLOCK_WORKAROUND)];
          // Restrict to only WORKAROUND for audit missing entries (websiteName diff)
          allowed = allowed.filter(a => a === ServiceSolution.ADBLOCK_WORKAROUND);
        }
        if (st === ServiceType.NO_CONSENT) {
          // Audit currently surfaces only OUTBRAIN diffs
            allowed = allowed.filter(a => a === ServiceSolution.NO_CONSENT_OUTBRAIN);
        }
        setFormData(prev => ({ ...prev, service: st, solution: (allowed[0] || '') as ServiceSolution }));
        
        // Pré-remplir le nom du domaine avec l'identifiant utilisé (pour création rapide)
        if (needsDomainSelection && missingService.identifierUsed) {
          setNewDomainName(missingService.identifierUsed);
          // Activer automatiquement le mode création de domaine pour les services sans domaine
          setCreateNewDomain(true);
        }
      } else {
        setFormData(prev => ({ ...prev, service: '' as any, solution: '' as any }));
      }
      
      // Load networks always
      if (needsDomainSelection || domain) {
        loadNetworks();
      }
    }
  }, [open, missingService, needsDomainSelection]);

  // Effet pour pré-sélectionner network/domain si fourni
  useEffect(() => {
    if (domain && networks.length > 0 && !needsDomainSelection) {
      // Trouver le network qui contient ce domaine
      for (const network of networks) {
        const networkDomains = (network as any).domains || [];
        if (networkDomains.some((d: Domain) => d.id === domain.id)) {
          setSelectedNetworkId(network.id);
          setDomains(networkDomains);
          setSelectedDomainId(domain.id);
          break;
        }
      }
    }
  }, [domain, networks, needsDomainSelection]);

  const loadNetworks = async () => {
    try {
      setLoadingNetworks(true);
      const { data } = await axiosInstance.get<Network[]>('/networks');
      setNetworks(data || []);
    } catch (e) {
      console.error('Erreur chargement networks:', e);
      setError(t('common.loadError', 'Erreur de chargement'));
    } finally {
      setLoadingNetworks(false);
    }
  };

  const loadDomainsForNetwork = async (networkId: number) => {
    try {
      setLoadingDomains(true);
      const { data } = await axiosInstance.get<Domain[]>(`/networks/${networkId}/domains`);
      setDomains(data || []);
    } catch (e) {
      console.error('Erreur chargement domains:', e);
      setError(t('common.loadError', 'Erreur de chargement'));
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleNetworkChange = (networkId: string) => {
    const id = parseInt(networkId);
    setSelectedNetworkId(id);
    setSelectedDomainId(null);
    setDomains([]);
    if (id && !createNewDomain) {
      loadDomainsForNetwork(id);
    }
  };

  const handleToggleCreateNewDomain = () => {
    setCreateNewDomain(!createNewDomain);
    setSelectedDomainId(null);
    if (!createNewDomain && missingService?.identifierUsed) {
      // Pré-remplir avec l'identifiant
      setNewDomainName(missingService.identifierUsed);
    }
  };

  if (!missingService) return null;

  const serviceType = missingService.missingService as ServiceType;
  // Compute allowed solutions (apply same restriction logic as in open effect for consistency)
  let allowedSolutions = SERVICE_TO_SOLUTIONS[serviceType] || [];
  if (serviceType === ServiceType.ADBLOCK) allowedSolutions = [ServiceSolution.ADBLOCK_WORKAROUND];
  if (serviceType === ServiceType.NO_CONSENT) allowedSolutions = [ServiceSolution.NO_CONSENT_OUTBRAIN];

  const handleSubmit = async () => {
    console.log('🔥 handleSubmit called!');
    try {
      setSubmitting(true);
      setError(null);
      
      console.log('🔍 Validation checks...');

      if (!formData.startDate) {
        console.log('❌ No start date');
        setError(t('domainServices.audit.quickAdd.errors.startDateRequired', 'Start date required'));
        return;
      }

      if (!formData.solution) {
        console.log('❌ No solution');
        setError(t('domainServices.audit.quickAdd.errors.solutionRequired', 'Solution required'));
        return;
      }

      if (formData.commissionPercentage != null) {
        const n = formData.commissionPercentage;
        if (isNaN(n) || n < 0 || n > 100) {
          console.log('❌ Invalid commission');
          setError(t('domainServices.audit.quickAdd.errors.invalidCommission', 'Invalid commission (0-100)'));
          return;
        }
      }

      // Mode création de nouveau domaine
      if (createNewDomain) {
        if (!selectedNetworkId) {
          setError(t('domainServices.audit.quickAdd.errors.networkRequired', 'Veuillez sélectionner un réseau'));
          return;
        }
        if (!newDomainName.trim()) {
          setError(t('domainServices.audit.quickAdd.errors.domainNameRequired', 'Veuillez saisir un nom de domaine'));
          return;
        }

        console.log('🏗️ Creating domain + service with quickCreateWithDomain');
        
        const payload: any = {
          networkId: selectedNetworkId,
          domainName: newDomainName.trim(),
          domainUrl: newDomainUrl.trim() || undefined,
          service: formData.service as ServiceType,
          solution: formData.solution as ServiceSolution,
          startDate: formData.startDate,
          endDate: formData.endDate ? formData.endDate : null,
          status: ServiceStatus.ACTIVE,
        };

        // Ajouter les configs spécifiques
        if (formData.solution === ServiceSolution.ADBLOCK_LEGACY) {
          if (formData.commissionPublisher != null) payload.commissionPublisher = formData.commissionPublisher;
          if (formData.commissionDblock != null) payload.commissionDblock = formData.commissionDblock;
        } else if (formData.commissionPercentage != null) {
          payload.commissionPercentage = formData.commissionPercentage;
        }
        if ([ServiceSolution.ADBLOCK_WORKAROUND, ServiceSolution.NO_CONSENT_GAM].includes(formData.solution as ServiceSolution)) {
          if (formData.gamType) payload.gamType = formData.gamType;
          if (formData.gamAccountId) payload.gamAccountId = formData.gamAccountId;
        }
        if (formData.solution === ServiceSolution.ADBLOCK_WORKAROUND && formData.scriptVariant) payload.scriptVariant = formData.scriptVariant;
        if (formData.solution === ServiceSolution.SSP) {
          if (formData.integrationType) payload.integrationType = formData.integrationType;
          if (formData.protocol) payload.protocol = formData.protocol;
        }
        
        // Utiliser identifierUsed pour les identifiants spécifiques
        if (formData.solution === ServiceSolution.NO_CONSENT_OUTBRAIN && missingService.identifierUsed) {
          payload.outbrainSection = missingService.identifierUsed;
        }
        if (formData.solution === ServiceSolution.ADBLOCK_WORKAROUND && missingService.identifierUsed) {
          payload.websiteName = missingService.identifierUsed;
        }
        if (formData.solution === ServiceSolution.SSP && missingService.identifierUsed) {
          payload.siteKey = missingService.identifierUsed;
        }

        console.log('📤 Sending quickCreateWithDomain payload:', payload);

        const result = await quickCreateWithDomain(payload);
        console.log('✅ Domain + Service created:', result);
        
        await onCreated();
        onOpenChange(false);
        return;
      }

      // Mode standard: sélection d'un domaine existant
      console.log('🏗️ Domain selection, selectedDomainId:', selectedDomainId);
      if (!selectedDomainId) {
        console.log('❌ No domain selected');
        setError(t('domainServices.audit.quickAdd.errors.domainRequired', 'Veuillez sélectionner un domaine'));
        return;
      }

      const payload: CreateServiceSubscriptionRequest = {
        domainId: selectedDomainId,
        service: formData.service as ServiceType,
        solution: formData.solution as ServiceSolution,
        startDate: formData.startDate,
        endDate: formData.endDate ? formData.endDate : null,
        status: ServiceStatus.ACTIVE,
      };

      if (formData.solution === ServiceSolution.ADBLOCK_LEGACY) {
        if (formData.commissionPublisher != null) payload.commissionPublisher = formData.commissionPublisher;
        if (formData.commissionDblock != null) payload.commissionDblock = formData.commissionDblock;
      } else if (formData.commissionPercentage != null) {
        payload.commissionPercentage = formData.commissionPercentage;
      }
      if ([ServiceSolution.ADBLOCK_WORKAROUND, ServiceSolution.NO_CONSENT_GAM].includes(formData.solution as ServiceSolution)) {
        if (formData.gamType) payload.gamType = formData.gamType;
        if (formData.gamAccountId) payload.gamAccountId = formData.gamAccountId;
      }
      if (formData.solution === ServiceSolution.ADBLOCK_WORKAROUND && formData.scriptVariant) payload.scriptVariant = formData.scriptVariant;
      if (formData.solution === ServiceSolution.SSP) {
        if (formData.integrationType) payload.integrationType = formData.integrationType;
        if (formData.protocol) payload.protocol = formData.protocol;
      }
      
      // Utiliser identifierUsed pour les identifiants spécifiques
      if (formData.solution === ServiceSolution.NO_CONSENT_OUTBRAIN && missingService.identifierUsed) {
        payload.outbrainSection = missingService.identifierUsed;
      }
      if (formData.solution === ServiceSolution.ADBLOCK_WORKAROUND && missingService.identifierUsed) {
        payload.websiteName = missingService.identifierUsed;
      }
      if (formData.solution === ServiceSolution.SSP && missingService.identifierUsed) {
        payload.siteKey = missingService.identifierUsed;
      }

      console.log('📤 Sending payload:', payload);

      const created = await createService(payload);
      console.log('✅ Service created:', created);
      
      if (created) {
        await onCreated();
        onOpenChange(false);
      }
    } catch (e: any) {
      console.error('💥 Quick add service error', e);
      setError(e?.response?.data?.error || t('domainServices.audit.quickAdd.error', 'Creation failed'));
    } finally {
      console.log('🏁 Setting submitting to false');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('domainServices.audit.quickAdd.title', 'Ajouter le service')}</DialogTitle>
          <DialogDescription>
            {needsDomainSelection ? 
              t('domainServices.audit.quickAdd.descriptionSection', 'Créer un service pour la section {{section}}', { section: missingService?.domain }) :
              t('domainServices.audit.quickAdd.description', 'Créer rapidement un service manquant pour le domaine {{domain}}', { domain: domain?.name })
            }
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Sélection Network/Domain */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">
                {createNewDomain ? 
                  t('domainServices.audit.quickAdd.createNewDomain', 'Créer un nouveau domaine') :
                  needsDomainSelection ? 
                    t('domainServices.audit.quickAdd.domainSelection', 'Sélection du domaine') :
                    t('domainServices.audit.quickAdd.domainAssignment', 'Attribution du domaine')
                }
              </h4>
              {/* Bouton pour basculer entre sélection et création */}
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleToggleCreateNewDomain}
                className="text-xs"
              >
                {createNewDomain ? (
                  <><Globe className="h-3 w-3 mr-1" /> {t('domainServices.audit.quickAdd.selectExisting', 'Sélectionner existant')}</>
                ) : (
                  <><Plus className="h-3 w-3 mr-1" /> {t('domainServices.audit.quickAdd.createNew', 'Créer nouveau')}</>
                )}
              </Button>
            </div>

            {createNewDomain ? (
              // Mode création de nouveau domaine
              <div className="space-y-4">
                <div>
                  <Label>{t('common.network', 'Réseau')} *</Label>
                  <Select value={selectedNetworkId?.toString() || ''} onValueChange={handleNetworkChange} disabled={loadingNetworks}>
                    <SelectTrigger>
                      <SelectValue placeholder={loadingNetworks ? t('common.loading') : t('common.selectNetwork', 'Sélectionner un réseau')} />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map(network => (
                        <SelectItem key={network.id} value={network.id.toString()}>{network.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('domainServices.audit.quickAdd.domainName', 'Nom du domaine')} *</Label>
                  <Input 
                    value={newDomainName} 
                    onChange={(e) => setNewDomainName(e.target.value)}
                    placeholder={t('domainServices.audit.quickAdd.domainNamePlaceholder', 'ex: example.com')}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('domainServices.audit.quickAdd.domainNameHint', 'Le nom sera utilisé comme identifiant unique dans ce réseau')}
                  </p>
                </div>
                <div>
                  <Label>{t('domainServices.audit.quickAdd.domainUrl', 'URL du domaine')} ({t('common.optional', 'optionnel')})</Label>
                  <Input 
                    value={newDomainUrl} 
                    onChange={(e) => setNewDomainUrl(e.target.value)}
                    placeholder={t('domainServices.audit.quickAdd.domainUrlPlaceholder', 'ex: https://example.com')}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('domainServices.audit.quickAdd.domainUrlHint', 'Si non renseigné, une URL sera générée automatiquement')}
                  </p>
                </div>
              </div>
            ) : (
              // Mode sélection de domaine existant
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('common.network', 'Réseau')}</Label>
                  <Select value={selectedNetworkId?.toString() || ''} onValueChange={handleNetworkChange} disabled={loadingNetworks}>
                    <SelectTrigger>
                      <SelectValue placeholder={loadingNetworks ? t('common.loading') : t('common.selectNetwork', 'Sélectionner un réseau')} />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map(network => (
                        <SelectItem key={network.id} value={network.id.toString()}>{network.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('common.domain', 'Domaine')}</Label>
                  <Select 
                    value={selectedDomainId?.toString() || ''} 
                    onValueChange={(v) => setSelectedDomainId(parseInt(v))} 
                    disabled={!selectedNetworkId || loadingDomains}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        !selectedNetworkId ? t('common.selectNetworkFirst', 'Sélectionnez d\'abord un réseau') :
                        loadingDomains ? t('common.loading') :
                        t('common.selectDomain', 'Sélectionner un domaine')
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map(domain => (
                        <SelectItem key={domain.id} value={domain.id.toString()}>{domain.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Info sur l'identifiant qui sera utilisé */}
            {missingService.identifierUsed && (
              <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-950 rounded text-xs">
                <span className="font-medium text-blue-700 dark:text-blue-300">
                  {t('domainServices.audit.quickAdd.identifierInfo', 'Identifiant à configurer:')}
                </span>
                <code className="ml-2 px-1 py-0.5 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200">
                  {missingService.identifierUsed}
                </code>
              </div>
            )}
          </div>

          <ServiceFormFields
            data={formData}
            onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
            lockService
            lockSolution
            allowedSolutionsOverride={allowedSolutions as ServiceSolution[]}
            mode="quickadd"
            identifierVisibility="hidden"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('domainServices.audit.quickAdd.startDate', 'Date de début')}</Label>
              <Input type="date" value={formData.startDate} onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>{t('domainServices.audit.quickAdd.endDate', 'Date de fin')}</Label>
              <Input type="date" value={formData.endDate} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))} className="mt-1" />
            </div>
          </div>
          
          {/* Commission slider maintenant géré dans ServiceFormFields */}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            <X className="h-4 w-4 mr-1" /> {t('domainServices.audit.quickAdd.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            <Save className="h-4 w-4 mr-1" /> {submitting ? t('common.saving', 'Saving...') : t('domainServices.audit.quickAdd.confirm', 'Créer')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
